import React, { useEffect, useState, useCallback } from "react";
import {
  PlusIcon,
  UsersIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilSquareIcon,
  TrashIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import {
  getAccountInfo,
  getAccounts, // ✅ NEW
  updateAccountPatch,
  deleteAccount,
  createAccount,
  getRoles,
} from "../../services/AccountService/AccountService";
import toast from "react-hot-toast";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { confirmToast } from "../../utils/confirmToast";

export const ManagementPage = () => {
  const [account, setAccount] = useState(null);
  const [loadingAccount, setLoadingAccount] = useState(true);

  // Users list from /auth/account/
  const [users, setUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // Pagination (simple)
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [count, setCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(count / pageSize));

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleUuid: "",
  });
  const [passwordVisibility, setPasswordVisibility] = useState({});

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [isEditFormOpen, setIsEditFormOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    roleUuid: "",
  });

  useEffect(() => {
    const loadRoles = async () => {
      try {
        setLoadingRoles(true);
        const rolesData = await getRoles();
        setRoles(rolesData.results || rolesData || []);
      } catch (err) {
        console.error(err);
        toast.error("Рөлдерді жүктеу қатесі.");
      } finally {
        setLoadingRoles(false);
      }
    };
    loadRoles();
  }, []);

  // Load banner data
  useEffect(() => {
    const loadInfo = async () => {
      try {
        setLoadingAccount(true);
        const info = await getAccountInfo();
        setAccount(info);
      } catch (err) {
        console.error(err);
        toast.error("Аккаунт мәліметтерін жүктеу сәтсіз аяқталды.");
      } finally {
        setLoadingAccount(false);
      }
    };
    loadInfo();
  }, []);

  // Load users list
  const loadUsers = useCallback(
    async (pageNum = 1) => {
      try {
        setListLoading(true);
        const data = await getAccounts({ page: pageNum, page_size: pageSize });
        // data shape: { count, next, previous, results: [...] }
        setUsers(
          (data.results || []).map((u) => ({
            uuid: u.uuid,
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            email: u.email || "",
            createdAt: u.created_at || "-", // if your BE includes it
            roles: u.roles || [],
          }))
        );
        setCount(data.count || 0);
        setPage(pageNum);
      } catch (err) {
        console.error(err);
        toast.error("Пайдаланушылар тізімін жүктеу қате.");
      } finally {
        setListLoading(false);
      }
    },
    [pageSize]
  );

  useEffect(() => {
    loadUsers(1);
  }, [loadUsers]);

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      firstName: user.first_name || "",
      lastName: user.last_name || "",
      email: user.email || "",
      roleUuid: user.roles?.[0]?.uuid || user.roles?.[0]?.id || "", // Take first role if multiple
    });
    setIsEditFormOpen(true);
  };

  // Update user handler
  const handleUpdateUser = async () => {
    if (!editingUser) return;

    // Basic validation
    if (!editFormData.firstName.trim()) {
      toast.error("Атын енгізіңіз");
      return;
    }
    if (!editFormData.lastName.trim()) {
      toast.error("Тегін енгізіңіз");
      return;
    }
    if (!editFormData.email.trim()) {
      toast.error("Email енгізіңіз");
      return;
    }
    if (!editFormData.roleUuid.trim()) {
      toast.error("Рөлді таңдаңыз");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email)) {
      toast.error("Дұрыс email форматын енгізіңіз");
      return;
    }

    try {
      setIsUpdating(true);

      // Update user with the data from form
      await updateAccountPatch(editingUser.uuid, {
        first_name: editFormData.firstName,
        last_name: editFormData.lastName,
        email: editFormData.email,
        role: editFormData.roleUuid,
      });

      toast.success("Пайдаланушы сәтті жаңартылды!");

      // Reset form and close modal
      setEditFormData({ firstName: "", lastName: "", email: "", roleUuid: "" });
      setIsEditFormOpen(false);
      setEditingUser(null);

      // Reload users list to show updated user
      await loadUsers(page);
    } catch (error) {
      console.error("Update user error:", error);
      toast.error("Пайдаланушыны жаңарту кезінде қате пайда болды");
    } finally {
      setIsUpdating(false);
    }
  };

  // Create user via POST /auth/account/ (only first_name, last_name, email)
  const handleAddUser = async () => {
    // Basic validation
    if (!formData.firstName.trim()) {
      toast.error("Атын енгізіңіз");
      return;
    }
    if (!formData.lastName.trim()) {
      toast.error("Тегін енгізіңіз");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email енгізіңіз");
      return;
    }
    if (!formData.roleUuid.trim()) {
      toast.error("Рөлді таңдаңыз");
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Дұрыс email форматын енгізіңіз");
      return;
    }

    try {
      setIsCreating(true);

      // Get organization UUID from current account
      const organizationUuid = account?.organization?.uuid;
      if (!organizationUuid) {
        toast.error("Ұйым мәліметтері табылмады");
        return;
      }

      // Create user with the data from form
      await createAccount(
        formData.firstName, // name
        formData.lastName, // surname
        formData.email, // email
        formData.roleUuid, // role UUID
        organizationUuid // organization UUID from current account
      );

      toast.success("Пайдаланушы сәтті жасалды!");

      // Reset form and close modal
      setFormData({ firstName: "", lastName: "", email: "", roleUuid: "" });
      setIsFormOpen(false);

      // Reload users list to show new user
      await loadUsers(1);
    } catch (error) {
      console.error("Create user error:", error);
      toast.error("Пайдаланушы жасау кезінде қате пайда болды");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (userUuid) => {
    const ok = await confirmToast({
      title: "Жоюды растау",
      message: "Бұл пайдаланушыны жойғыңыз келетініне сенімдісіз бе?",
      confirmText: "ИӘ, ЖОЮ",
      cancelText: "БАС ТАРТУ",
    });
    if (!ok) return;

    try {
      await deleteAccount(userUuid);
      toast.success("Пайдаланушы сәтті жойылды.");
      const data = await loadUsers(page);
      if ((data?.results?.length ?? 0) === 0 && page > 1) {
        await loadUsers(page - 1);
      }
    } catch (e) {
      console.error(e);
      toast.error("Жою кезінде қате пайда болды.");
    }
  };

  const togglePasswordVisibility = (userUuid) =>
    setPasswordVisibility((p) => ({ ...p, [userUuid]: !p[userUuid] }));

  const fullName =
    account?.last_name || account?.first_name
      ? `${account?.last_name || ""} ${account?.first_name || ""}`.trim()
      : null;

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Block (org name + account owner + roles) */}
        <div className="bg-white rounded-3xl p-8 mb-8 border border-gray-200 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-br from-[#674DA2] to-[#5a4291] p-4 rounded-2xl">
                <BuildingOfficeIcon className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {loadingAccount
                    ? "Жүктелуде..."
                    : account?.organization?.name || "—"}
                </h1>
                {!loadingAccount && (
                  <>
                    <p className="text-gray-600">
                      Аккаунт иесі: {fullName || account?.email || "—"}
                    </p>
                    <p className="text-gray-600">
                      Рөлі:{" "}
                      {account?.roles?.length
                        ? account.roles.map((r) => r.name).join(", ")
                        : "—"}
                    </p>
                  </>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="bg-[#674DA2] bg-opacity-20 rounded-2xl p-4">
                <div className="text-2xl font-bold text-[#674DA2]">
                  {listLoading ? "…" : users.length} /{" "}
                  {account?.organization?.max_accounts}
                </div>
                <div className="text-[#674DA2] text-sm">Пайдаланушылар</div>
              </div>
            </div>
          </div>
        </div>
        {/* Add User Button */}
        <div className="mb-8">
          <button
            onClick={() => setIsFormOpen(true)}
            disabled={count >= (account?.organization?.max_accounts || 0)}
            className={`${
              count >= (account?.organization?.max_accounts || 0)
                ? "bg-gray-400 cursor-not-allowed opacity-60"
                : "bg-gradient-to-r from-[#674DA2] to-[#5a4291] hover:from-[#5a4291] hover:to-[#4d3e7c] transform hover:scale-105 hover:shadow-xl"
            } text-white px-8 py-4 rounded-2xl font-semibold flex items-center space-x-2 transition-all duration-300 shadow-lg`}
          >
            <PlusIcon className="w-5 h-5" />
            <span>
              {count >= (account?.organization?.max_accounts || 0)
                ? "Максималды лимитке жетті"
                : "Пайдаланушы қосу"}
            </span>
          </button>

          {/* Optional: Show current/max count */}
          {account?.organization?.max_accounts &&
            count >= account.organization.max_accounts && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-3">
                <div className="flex items-start space-x-3">
                  <InformationCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-amber-800 mb-1">
                      Аккаунт лимитіне жеттіңіз
                    </h4>
                    <p className="text-sm text-amber-700">
                      Сіздің ұйымда максималды{" "}
                      {account.organization.max_accounts} пайдаланушы болуы
                      мүмкін. Жаңа пайдаланушы қосу үшін алдымен бір аккаунтты
                      жою керек.
                    </p>
                  </div>
                </div>
              </div>
            )}
        </div>
        {/* Modal Form */}
        {isFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl transform transition-all">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Жаңа пайдаланушы қосу
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Аты
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="Атын енгізіңіз"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Тегі
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="Тегін енгізіңіз"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Рөлі
                  </label>
                  <select
                    value={formData.roleUuid}
                    onChange={(e) =>
                      setFormData({ ...formData, roleUuid: e.target.value })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    disabled={loadingRoles}
                  >
                    <option value="">
                      {loadingRoles ? "Рөлдер жүктелуде..." : "Рөлді таңдаңыз"}
                    </option>
                    {roles.map((role) => (
                      <option
                        key={role.uuid || role.id}
                        value={role.uuid || role.id}
                      >
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex space-x-4 mt-4">
                  <button
                    onClick={handleAddUser}
                    disabled={isCreating}
                    className={`${
                      isCreating
                        ? "opacity-70 cursor-not-allowed"
                        : "hover:from-[#5a4291] hover:to-[#4d3e7c] transform hover:scale-105"
                    } bg-gradient-to-r from-[#674DA2] to-[#5a4291] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1`}
                  >
                    {isCreating ? "Жасалуда..." : "Пайдаланушы жасау"}
                  </button>
                  <button
                    onClick={() => {
                      if (isCreating) return;
                      setIsFormOpen(false);
                      setFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        roleUuid: "",
                      });
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1"
                  >
                    Бас тарту
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {isEditFormOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl transform transition-all">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Пайдаланушыны өңдеу
              </h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Аты
                  </label>
                  <input
                    type="text"
                    value={editFormData.firstName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        firstName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="Атын енгізіңіз"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Тегі
                  </label>
                  <input
                    type="text"
                    value={editFormData.lastName}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        lastName: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="Тегін енгізіңіз"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        email: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Рөлі
                  </label>
                  <select
                    value={editFormData.roleUuid}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        roleUuid: e.target.value,
                      })
                    }
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#674DA2] focus:border-transparent"
                    disabled={loadingRoles}
                  >
                    <option value="">
                      {loadingRoles ? "Рөлдер жүктелуде..." : "Рөлді таңдаңыз"}
                    </option>
                    {roles.map((role) => (
                      <option
                        key={role.uuid || role.id}
                        value={role.uuid || role.id}
                      >
                        {role.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex space-x-4 mt-4">
                  <button
                    onClick={handleUpdateUser}
                    disabled={isUpdating}
                    className={`${
                      isUpdating
                        ? "opacity-70 cursor-not-allowed"
                        : "hover:from-[#5a4291] hover:to-[#4d3e7c] transform hover:scale-105"
                    } bg-gradient-to-r from-[#674DA2] to-[#5a4291] text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1`}
                  >
                    {isUpdating ? "Жаңартылуда..." : "Өзгерістерді сақтау"}
                  </button>
                  <button
                    onClick={() => {
                      if (isUpdating) return;
                      setIsEditFormOpen(false);
                      setEditingUser(null);
                      setEditFormData({
                        firstName: "",
                        lastName: "",
                        email: "",
                        roleUuid: "",
                      });
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex-1"
                  >
                    Бас тарту
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Users Table */}
        <div className="bg-white rounded-3xl border border-gray-200 shadow-md overflow-hidden">
          <div className="p-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <UsersIcon className="w-6 h-6 text-[#674DA2]" />
                <h2 className="text-2xl font-bold text-gray-900">
                  Пайдаланушылар тізімі
                </h2>
              </div>
              <div className="text-sm text-gray-500">
                {listLoading ? "Жүктелуде..." : `Барлығы: ${count}`}
              </div>
            </div>

            {users.length === 0 ? (
              <div className="text-center py-12">
                <UsersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">
                  Пайдаланушылар табылмады
                </p>
                <p className="text-gray-400">
                  "Пайдаланушы қосу" түймесін басып бастаңыз
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-4 px-6 text-gray-700 font-semibold">
                          Тегі
                        </th>
                        <th className="text-left py-4 px-6 text-gray-700 font-semibold">
                          Аты
                        </th>
                        <th className="text-left py-4 px-6 text-gray-700 font-semibold">
                          Email
                        </th>
                        <th className="text-left py-4 px-6 text-gray-700 font-semibold">
                          Рөлі
                        </th>
                        <th className="text-left py-4 px-6 text-gray-700 font-semibold">
                          Әрекеттер
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user.uuid}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-4 px-6 text-gray-800">
                            {user.last_name || "—"}
                          </td>
                          <td className="py-4 px-6 text-gray-800">
                            {user.first_name || "—"}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center space-x-2">
                              <EnvelopeIcon className="w-4 h-4 text-[#674DA2]" />
                              <span className="text-gray-800">
                                {user.email || "—"}
                              </span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-gray-800">
                            {user.roles?.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {user.roles.map((role, index) => (
                                  <span
                                    key={role.uuid || role.id || index}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#674DA2] bg-opacity-10 text-[#674DA2] border border-[#674DA2] border-opacity-20"
                                  >
                                    {role.name || "—"}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">
                                Рөл жоқ
                              </span>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex space-x-2">
                              <button
                                title="Өңдеу"
                                onClick={() => handleEditUser(user)}
                                className="text-[#674DA2] hover:text-[#5a4291] transition-colors p-2 hover:bg-[#674DA2] hover:bg-opacity-10 rounded-lg"
                              >
                                <PencilSquareIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteUser(user.uuid)}
                                className="text-red-500 hover:text-red-700 transition-colors p-2 hover:bg-red-50 rounded-lg"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Simple Pagination */}
                <div className="flex items-center justify-between mt-6">
                  <button
                    disabled={page <= 1 || listLoading}
                    onClick={() => loadUsers(page - 1)}
                    className={`px-4 py-2 rounded-lg border ${
                      page <= 1 || listLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    ← Алдыңғы
                  </button>
                  <div className="text-sm text-gray-600">
                    {page} / {totalPages}
                  </div>
                  <button
                    disabled={page >= totalPages || listLoading}
                    onClick={() => loadUsers(page + 1)}
                    className={`px-4 py-2 rounded-lg border ${
                      page >= totalPages || listLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:bg-gray-50"
                    }`}
                  >
                    Келесі →
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManagementPage;
